// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { type CamelCasedPropertiesDeep } from 'type-fest';

import {
  type GetProductListingBySlugResponse,
  type GetSimilarListingFromProductBySlug,
  type ProductBySlugResponse,
  type ProductBySlugExtendedResponse,
  type ProductOfferStatus,
  type RepopPicturesData,
  type ShippingConfidence,
} from './aliases';

import { type CurrencyCode } from '@/modules/currencies/constants';
import { type DisplayPrice } from '@/modules/price/aliases';
import { type TransformedPriceEstimateDictionary } from '@/modules/price/types.ts';
import { type ShippingMethod } from '@/modules/shipping/types';

export type ShippingConfidenceLevel = ShippingConfidence['level'];

export const ShippingConfidenceLevel = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const satisfies Record<ShippingConfidenceLevel, ShippingConfidenceLevel>;

export type ThumbnailFormat = {
  url: string;
  width: number;
  height: number;
};

export type PictureFormat = ThumbnailFormat & {
  id: number;
};

export type PictureFormatV2 = ThumbnailFormat & {
  id: string;
  initialResolutionUrl?: string;
};

export enum LegacyProductStatus {
  ONSALE = 'ONSALE',
  PURCHASED = 'PURCHASED',
  DELETED = 'DELETED',
  DELETED_ONSALE = 'DELETED_ONSALE',
  DELETED_PURCHASED = 'DELETED_PURCHASED',
  DELETED_MARKED_AS_SOLD = 'DELETED_MARKED_AS_SOLD',
  MARKED_AS_SOLD = 'MARKED_AS_SOLD',
  PRODUCT_BANNED_ONSALE = 'PRODUCT_BANNED_ONSALE',
  USER_BANNED_ONSALE = 'USER_BANNED_ONSALE',
  PRODUCT_BANNED_PURCHASED = 'PRODUCT_BANNED_PURCHASED',
  USER_BANNED_PURCHASED = 'USER_BANNED_PURCHASED',
  IN_REVIEW = 'IN_REVIEW',
  PRODUCT_BANNED_IN_REVIEW = 'PRODUCT_BANNED_IN_REVIEW',
  USER_BANNED_IN_REVIEW = 'USER_BANNED_IN_REVIEW',
  DELETED_IN_REVIEW = 'DELETED_IN_REVIEW',
}

export enum ProductStatus {
  ONSALE = 'S',
  PURCHASED = 'P',
  DELETED = 'X',
  DELETED_ONSALE = 'x',
  DELETED_PURCHASED = 'p',
  DELETED_MARKED_AS_SOLD = 'm',
  MARKED_AS_SOLD = 'M',
  PRODUCT_BANNED_ONSALE = 'B',
  USER_BANNED_ONSALE = 'b',
  PRODUCT_BANNED_PURCHASED = 'C',
  USER_BANNED_PURCHASED = 'c',
  IN_REVIEW = 'R',
  PRODUCT_BANNED_IN_REVIEW = 'E',
  USER_BANNED_IN_REVIEW = 'e',
  DELETED_IN_REVIEW = 'r',
  // USER_INACTIVE does not have a database char equivalent
  USER_INACTIVE = 'USER_INACTIVE',
}

export type AnyProductStatus =
  ProductStatus | LegacyProductStatus | `${BackendProductStatus}`;

export enum BackendProductStatus {
  ONSALE = 'STATUS_ONSALE',
  PURCHASED = 'STATUS_PURCHASED',
  MARKED_AS_SOLD = 'STATUS_MARKED_AS_SOLD',
  DELETED = 'STATUS_DELETED',
  DELETED_ONSALE = 'STATUS_DELETED_ONSALE',
  DELETED_PURCHASED = 'STATUS_DELETED_PURCHASED',
  DELETED_MARKED_AS_SOLD = 'STATUS_DELETED_MARKED_AS_SOLD',
  PRODUCT_BANNED_ONSALE = 'STATUS_PRODUCT_BANNED_ONSALE',
  USER_BANNED_ONSALE = 'STATUS_USER_BANNED_ONSALE',
  PRODUCT_BANNED_PURCHASED = 'STATUS_PRODUCT_BANNED_PURCHASED',
  USER_BANNED_PURCHASED = 'STATUS_USER_BANNED_PURCHASED',
  IN_REVIEW = 'STATUS_IN_REVIEW',
  PRODUCT_BANNED_IN_REVIEW = 'STATUS_PRODUCT_BANNED_IN_REVIEW',
  USER_BANNED_IN_REVIEW = 'STATUS_USER_BANNED_IN_REVIEW',
  DELETED_IN_REVIEW = 'STATUS_DELETED_IN_REVIEW',
}

export type Price = {
  currencyName: CurrencyCode;
  priceAmount: string;
  discountedPriceAmount?: string;
  discountPercentage?: number | string;
  internationalShippingCost?: string;
  nationalShippingCost?: string;
  taxInclusivePriceAmount?: string;
  taxInclusiveDiscountedPriceAmount?: string;
};

export type LegacyPrice = {
  currency_name: CurrencyCode;
  price_amount: string;
  currency_symbol: string;
  discount_percentage?: string;
  discounted_price_amount?: string;
  international_shipping_cost?: string;
  national_shipping_cost?: string;
  tax_inclusive_price_amount?: string;
  tax_inclusive_discounted_price_amount?: string;
};

export const OfferStatus = {
  ACCEPTED: 'ACCEPTED',
  EXPIRED: 'EXPIRED',
  BUYER_DISMISSED: 'BUYER_DISMISSED',
  OFFERER_COUNTERED: 'OFFERER_COUNTERED',
  BINDING_OFFERER_COUNTERED: 'BINDING_OFFERER_COUNTERED',
  PENDING: 'PENDING',
  BINDING_PENDING: 'BINDING_PENDING',
  REDEEMED: 'REDEEMED',
  REJECTED: 'REJECTED',
  RESCINDED: 'RESCINDED',
  SELLER_COUNTERED: 'SELLER_COUNTERED',
  SELLER_INITIATED: 'SELLER_INITIATED',
  UNAVAILABLE: 'UNAVAILABLE',
} as const satisfies Record<ProductOfferStatus, ProductOfferStatus>;

export type Preview = Partial<{
  150: string;
  210: string;
  320: string;
  480: string;
  640: string;
  960: string;
  1280: string;
}>;

export type FormattedVideos = {
  sources: Array<{
    url: string;
    format: string;
  }>;
  thumbnails: Array<ThumbnailFormat>;
};

export type Seller = {
  verified: boolean;
  initials: string;
  id: number;
  picture?: PictureFormat[];
  username: string;
  reviewsTotal?: number;
  reviewsRating?: number;
  itemsSold?: number;
  lastSeen?: string;
  firstName: string;
  lastName: string;
  averageShippingDays?: number;
};

export type SearchListProduct = {
  id: number;
  slug: string;
  pricing: TransformedPriceEstimateDictionary;
  has_video: boolean;
  preview: Preview;
  pictures: Preview[];
  status: string;
  sold?: boolean;
  variant_set_id?: number;
  variants?: Record<string, number>;
  is_liked?: boolean;
  like_count?: number;
  seller_user_name?: string;
  reviews_rating?: number;
  total_reviews?: number;
  is_boosted?: boolean;
  page_number?: number;
  sizes?: string[];
  brand_id?: number;
  brand_name?: string;
  description?: string;
  purchase_ids?: number[];
  product_type?: string;
};

export type ListProduct = {
  id: number;
  slug: string;
  price: Pick<Price, 'currencyName' | 'discountedPriceAmount' | 'priceAmount'>;
  displayPrice?: DisplayPrice;
  hasVideo: boolean;
  preview: Preview;
  dateCreated?: string;
  pictures?: Preview[];
  status: string;
  sold?: boolean;
  variantSetId?: number;
  variants?: Record<string, number>;
  isLiked?: boolean;
  likeCount?: number;
  sellerUserName?: string;
  reviewsRating?: number;
  totalReviews?: number;
  isBoosted?: boolean;
  pageNumber?: number;
  sizes?: string[];
  brandId?: number;
  brandName?: string;
  description?: string;
  purchaseIds?: number[];
};

export type LegacyListProduct = {
  id: number;
  slug: string;
  price: Price;
  hasVideo: boolean;
  preview: Preview;
  dateCreated?: string;
  pictures?: Preview[];
  status: string;
  sold?: boolean;
  variantSetId?: number;
  variants?: Record<string, number>;
  isLiked?: boolean;
  sellerUserName?: string;
  reviewsRating?: number;
  totalReviews?: number;
  isBoosted?: boolean;
  pageNumber?: number;
  sizes?: string[];
  brandId?: number;
  brandName?: string;
  description?: string;
  purchaseIds?: number[];
};

export type LegacyProductById = {
  id: number;
  address: string;
  description: string;
  pictures: PictureFormat[][];
  quantity?: number | null;
  variantSetId?: number | null;
  countryCode: string;
  status: ProductStatus | LegacyProductStatus;
  productType: string;
  slug: string;
  price: Price;
  dateUpdated: string;
  videos?: FormattedVideos[];
  seller: Seller;
  shippingMethods: ShippingMethod[];
  likeCount?: number;
  brandId: number | null;
  brandName?: string;
  isLiked?: boolean;
  isSaved?: boolean;
  variants?: Record<number, number>;
};

export type StyleAttribute = {
  id: string;
  name: string;
};

export type Size = {
  id: string;
  name: string;
  quantity: number;
};

export type Attributes = Record<string, string[]>;

type ProductAttribute = {
  id: string;
  name: string;
};

export type ProductBrandAttribute = { is_known: boolean } & ProductAttribute;

export type ProductSize = {
  id: number;
  name: string;
  quantity: number;
  status: BackendProductStatus;
};

export type ProductMediaAssetFormat = Record<string, ThumbnailFormat>;

export type ProductVideo = {
  id: string;
  video_url: string;
  thumbnail: {
    formats: ProductMediaAssetFormat;
  };
  outputs: Array<{
    format: string;
    url: string;
  }>;
};

export type ProductPicture = {
  id: number;
  original_width?: number;
  original_height?: number;
  is_square: boolean;
  formats: ProductMediaAssetFormat;
};

export type FormattedPicture = {
  id: string | number;
  width: number;
  height: number;
  url: string;
  initial_resolution_url?: string;
};

export type Seller_snake = {
  verified: boolean;
  initials: string;
  id: number;
  picture?: FormattedPicture[];
  username: string;
  reviews_total?: number;
  reviews_rating?: number;
  items_sold?: number;
  last_seen?: string;
  first_name: string;
  last_name: string;
  average_shipping_days?: number;
};

// Not the whole response, merely what we care about
export type UserProductBySlug = {
  id: number;
  isBoosted: boolean;
  description: string;
  pictures: PictureFormat[][];
  sizes?: Size[];
  pricing: CamelCasedPropertiesDeep<TransformedPriceEstimateDictionary>;
  organicClicks?: number;
  boostedClicks?: number;
  organicImpressions?: number;
  boostedImpressions?: number;
  sku?: string;
};

export type ListProductPrice = {
  priceAmount: string;
  currencyName: CurrencyCode;
  nationalShippingCost?: string;
  internationalShippingCost?: string;
};

export type ProductListingBySlugCamelCase =
  CamelCasedPropertiesDeep<GetProductListingBySlugResponse>;

export type SimilarProductListingBySlugCamelCase =
  CamelCasedPropertiesDeep<GetSimilarListingFromProductBySlug>;

export type RepopMetadata = {
  originalProductId: number;
  originalSellerId: number;
  purchaseItemId: number;
};

/*
    Note: The product by id endpoint also returns deprecated category_id and category_name fields
    which we should not use. They are therefore omitted from the type.
*/
export interface FormattedRepopProduct {
  id: number;
  brandId?: number;
  categoryId?: number;
  description: string;
  variants?: { [key: string]: number };
  variantSetId?: number;
  quantity?: number;
  condition?: string;
  colour?: string[];
  age?: string[];
  source?: string[];
  style?: string[];
  brand?: string;
  gender?: string;
  isKids?: boolean;
  productType?: string;
  group?: string;
  attributes?: Attributes;
  repopMetadata: RepopMetadata;
  repopPicturesData?: RepopPicturesData;
}

export type ProductPageProduct = Omit<ProductBySlugResponse, 'attributes'> &
  ProductBySlugExtendedResponse;

export type ProductCarouselInteraction =
  | 'ProductCarouselImageThumbnailClick'
  | 'ProductCarouselDotClick'
  | 'ProductImageArrowRight'
  | 'ProductImageArrowLeft'
  | 'ProductImageSwipeRight'
  | 'ProductImageSwipeLeft';

export type ProductPrice = {
  isReduced: boolean;
  currencyName: CurrencyCode;
  baseCurrentPrice: string;
  baseOriginalPrice: string;
  totalCurrentPrice: string;
  totalOriginalPrice: string;
  currentPriceFees?: string;
  currentSalesTax?: string;
  shippingCost?: string;
  inclShippingFee?: boolean;
  inclTax?: boolean;
  inclBuyerFee?: boolean;
};

export type YouMightBeIntoLocalStorageItem = {
  groupId: string;
  brandId: number;
  isKids: boolean;
  gender?: string;
  productType?: string;
};

type OmitLegacyCategoryFields<T> = Omit<
  T,
  'category' | 'categoryId' | 'category_id' | 'categoryName' | 'category_name'
>;

export type DeepOmitLegacyCategoryFields<T> = T extends unknown[]
  ? DeepOmitLegacyCategoryFields<T[number]>[]
  : T extends object
    ? OmitLegacyCategoryFields<{
        [K in keyof T]: DeepOmitLegacyCategoryFields<T[K]>;
      }>
    : T;

export type ProductFilters = {
  sizes?: string[];
  colours?: string[];
  minPrice?: number;
  maxPrice?: number;
  onSale?: boolean;
};
